import React from "react";
import CustomButton from "../ui/CustomButton";
import { Users } from "lucide-react";
import CurrencyIcon from "../ui/CurrencyIcon";
import { DEMO_AMOUNT_LABEL, formatCurrencyAmount, isAmountHidden, resolveDisplayCurrency } from "../../common/currencies";

const TournamentCard = ({ tournament, onPlayNow, variant = "cash" }) => {
  const { title, bannerUrl } = tournament;
  const displayCurrency = resolveDisplayCurrency(tournament?.currencyCode);
  const hideAmounts = isAmountHidden(tournament?.currencyCode);

  const getCardStyle = () => {
    let baseBg = 'linear-gradient(180deg, var(--black-shade3) 0%, var(--black-shade2) 100%)';
    if (variant === "practice") {
      baseBg = 'linear-gradient(180deg, var(--pink-dark-shade) 0%, var(--pink-light-shade) 100%)';
    }
    const borderStyle = {
      borderTopWidth: '0.5px',
      borderTopStyle: 'solid',
      borderImageSource: 'linear-gradient(90deg, var(--black-shade4) 0%, var(--text-dark) 53.85%, var(--black-shade4) 100%)',
      borderImageSlice: '1 0 0 0',
    };

    return {
      background: baseBg,
      ...borderStyle
    };
  };

  const isFeatured = variant === "featured";

  const customMappedData = {
    title: tournament?.description || "-",
    prizePool: formatCurrencyAmount(tournament?.winAmount, displayCurrency),
    entryFee: formatCurrencyAmount(tournament?.entryFee, displayCurrency),
    maxPlayers: tournament?.numPlayers || 0,
  };

  return (
    <div
      style={getCardStyle()}
      className={`rounded-app transition-all duration-300 mb-4 group hover:brightness-110 overflow-hidden flex flex-col w-full min-w-0`}
    >
      {/* Banner Image - Featured Only */}
      {isFeatured && bannerUrl && (
        <div className="w-full aspect-[21/9] sm:aspect-auto sm:h-36 overflow-hidden">
          <img
            src={bannerUrl}
            alt={title}
            className="w-full h-full object-fill group-hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}

      {/* Main Info Row */}
      <div className="p-3 flex items-center gap-2 w-full min-w-0">
        {/* Prize Section */}
        <div className="flex flex-col items-center justify-center min-w-[50px] ">
          <span className="text-[20px] font-black flex items-center gap-1 tracking-tight font-saira">
            {hideAmounts ? DEMO_AMOUNT_LABEL.amount : (
              <>
                <CurrencyIcon code={displayCurrency} className="w-5 h-5 object-contain" />
                {customMappedData?.prizePool}
              </>
            )}
          </span>
          <span className={`text-xs sm:text-sm ${variant === "practice" ? "text-whiteShade" : "text-darkGray"} font-medium mt-0.5`}>
            Prize Pool
          </span>
        </div>

        {/* Vertical Divider */}
        <div
          className="w-[1px] h-8 sm:h-10 self-center opacity-50 sm:opacity-100"
          style={{ background: 'linear-gradient(180deg, rgba(99, 99, 99, 0) 0%, rgba(99, 99, 99, 1) 48.56%, rgba(99, 99, 99, 0) 100%)' }}
        />

        {/* Details Section */}
        <div className="flex-1 flex flex-col justify-center pl-1 sm:pl-2 min-w-0">
          <h3 className="text-[20px] font-bold uppercase mb-0.5 sm:mb-1 truncate sm:whitespace-normal text-whiteShade font-saira">
            {customMappedData?.title}
          </h3>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-base font-extrabold font-inter">
            {!hideAmounts && (
              <div className="flex items-center gap-1 shrink-0">
                <span className={`text-xs sm:text-sm font-bold ${variant === "practice" ? "text-whiteShade" : "text-[var(--text-light)]"}`}>
                  {/* {variant === "featured" ? `${currentPlayers}/${maxPlayers} ` : `${maxPlayers}`} */}
                  {customMappedData?.maxPlayers}
                </span>
                <Users className={`w-3 h-3 sm:w-4 sm:h-4 ${variant === "practice" ? "text-pink-shade" : "text-[var(--text-dark)]"}`} />
              </div>
            )}
            <div className="flex items-center gap-1 shrink-0">
              <span className={`text-xs sm:text-sm font-bold flex justify-center items-center gap-1 ${variant === "practice" ? "text-whiteShade" : "text-[var(--text-light)]"}`}>
                {hideAmounts ? DEMO_AMOUNT_LABEL.entry : (
                  <>
                    <CurrencyIcon code={displayCurrency} className="w-4 h-4 sm:w-4 sm:h-4 object-contain" />
                    {customMappedData?.entryFee}
                  </>
                )}
              </span>
              <span className={`text-xs sm:text-sm font-medium ${variant === "practice" ? "text-whiteShade" : "text-[var(--text-dark)]"}`}>Entry</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex flex-col items-center justify-center shrink-0">
          <CustomButton
            onClick={onPlayNow}
            color={variant === "practice" ? "practice" : "green"}
            className="rounded-full px-3 font-extrabold font-saira"
          >
            PLAY NOW
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default TournamentCard;